import type { FlexProps } from '@hover/blueprint';
import { Body, Flex, Heading, forwardRef } from '@hover/blueprint';
import React from 'react';

export interface SwatchLabelProps extends FlexProps {
  primaryLabel: React.ReactNode;
  secondaryLabel: React.ReactNode;
  tertiaryLabel?: React.ReactNode;
  isDark?: boolean;
}

/**
 * SwatchLabel is a generic implementation (agnostic of data source). Do not
 * add props that require usage of a specific data source. Create or
 * use a wrapper that destructures the data source into the props needed for this
 * component instead.
 *
 * @see ./VariationEditSwatchLabel.tsx for a wrapper example
 */
export const SwatchLabel = forwardRef<SwatchLabelProps, 'div'>(
  (
    { primaryLabel, secondaryLabel, tertiaryLabel, isDark = false, ...props },
    ref,
  ) => (
    <Flex
      alignItems="stretch"
      color="neutral.0"
      direction="column"
      flex={1}
      justifyContent="center"
      {...props}
      ref={ref}
    >
      <Heading
        color="inherit"
        fontWeight="body"
        margin={0}
        noOfLines={1}
        size="xs"
      >
        {primaryLabel}
      </Heading>
      <Body
        color="inherit"
        fontSize={200}
        lineHeight={200}
        margin={0}
        noOfLines={1}
      >
        {secondaryLabel}
      </Body>
      {tertiaryLabel && (
        <Body
          color={isDark ? 'text.secondaryInvert' : 'text.secondary'}
          m={0}
          noOfLines={1}
          size="small"
        >
          {tertiaryLabel}
        </Body>
      )}
    </Flex>
  ),
);
